// src/lib/payment-derivation.js — Dérivation paiement/caution source-aware (ESM, client).
//
// ⚠️ MIROIR CANONIQUE. Garder STRICTEMENT en sync avec lib/payment-derivation.js
// (CJS serveur). CRA bloque l'import racine `lib/` depuis `src/` → duplication
// contrôlée, comme findLogementInCfg (cf. lib/messages-engine.js) et
// template-variables.js. Un test Jest (src/lib/payment-derivation.sync.test.js)
// asserte un résultat IDENTIQUE sur une matrice de cas pour détecter tout drift.
//
// CONTEXTE : les conditions de messages lisaient reservations.payment_received_at
// / caution_received_at, deux timestamps VIDES en prod → messages inertes et faux
// pour Airbnb (paiement via AirCover, pas Stripe). On dérive l'état réel depuis
// la source + les statuts métier.
//
// RÈGLE MÉTIER VALIDÉE (Dimitri) :
//   sejour_paye  = (source ∈ {airbnb, booking} ET status = 'confirmed')
//                  OU (source = 'direct' ET paiement_sejour_status = 'succeeded')
//   caution_recue = source = 'airbnb' (AirCover)
//                  OU caution_status ∈ CAUTION_RECUE_STATUSES — aligné
//                     _CAUTION_SATISFIED livret ; D2 GO Dimitri 2026-07-13.
//                     Fix B1 (verrou livret v2, 2026-07-20) : le VRAI
//                     caution_status Stripe fait foi pour TOUTE source non
//                     airbnb — booking, direct ET fail-safe (ical/csv/
//                     inconnues). Avant, une caution Stripe posée sur une résa
//                     ical restait « non reçue » à vie (filtre par source).
//
// Fonctions PURES, sans I/O. Tolérantes à la casse + variants iCal V1
// (booking_com / bookingdotcom) pour cohérence avec CAUTION_SOURCES_ELIGIBLE.

// Normalise la source vers l'une des catégories canoniques : 'airbnb' | 'booking'
// | 'direct' | autre (chaîne brute minusculée). Couvre les variants V1.
export function normalizeSource(rawSource) {
  const s = (rawSource || "").toLowerCase().trim();
  if (s === "airbnb") return "airbnb";
  if (s === "booking" || s === "booking_com" || s === "bookingdotcom")
    return "booking";
  if (s === "direct") return "direct";
  return s;
}

// sejourPaye(resa) → booléen.
//   OTA (airbnb/booking) : le séjour est réglé à la plateforme dès confirmation.
//   direct : le séjour est réglé quand le paiement Stripe a réussi.
export function sejourPaye(resa) {
  if (!resa || typeof resa !== "object") return false;
  const source = normalizeSource(resa.source);
  const status = (resa.status || "").toLowerCase();

  if ((source === "airbnb" || source === "booking") && status === "confirmed") {
    return true;
  }
  if (source === "direct" && resa.paiement_sejour_status === "succeeded") {
    return true;
  }
  return false;
}

// ── Statuts caution « reçue » — fix D2 (GO Dimitri 2026-07-13) ──
// ALIGNÉ sur _CAUTION_SATISFIED du livret (lib/livret-sections-handlers.js,
// ADR-174 PR4 — source de cohérence produit, parité assertée par
// src/lib/payment-derivation.sync.test.js) : la caution compte « reçue » dès
// que l'argent a été sécurisé, encaissé ou historiquement honoré pendant le
// séjour. Justification statut par statut :
//   authorized         → empreinte posée (hold Stripe actif) : argent sécurisé.
//   capture_pending    → capture en vol (lock optimiste api/stripe.js entre
//                        authorized et captured) : état transitoire entre deux
//                        statuts « reçus » — l'exclure ferait basculer la résa
//                        en « non reçue » le temps de la capture.
//   captured           → hold encaissé (résolution litige) : argent encaissé.
//   charged            → débit réel long séjour >4 nuits (ADR-174 PR3, capture
//                        automatique car un hold expire à 7 jours) : LE trou D2
//                        — le voyageur a PAYÉ sa caution ; sans ce statut,
//                        Welcome bloqué à tort + relance caution à un voyageur
//                        qui a payé.
//   released           → hold relâché en fin de séjour sans litige : la caution
//                        a bien été posée et honorée pendant le séjour.
//   refund_scheduled   → caution débitée (long séjour terminé), remboursement
//                        J+2 programmé : reçue pendant le séjour.
//   refunded /
//   partially_refunded → débitée puis remboursée (totalement / partiellement) :
//                        historiquement reçue et honorée.
//   refund_pending     → remboursement partiel EN VOL (verrou optimiste
//                        api/stripe.js, 2026-09-15) : l'argent est encore
//                        encaissé, l'état est transitoire — même logique que
//                        capture_pending.
//   refund_cancelled   → remboursement annulé : l'argent reste encaissé.
// EXCLUS (caution jamais mise en place — seuls cas où relancer le voyageur est
// légitime) : null / '' (aucune tentative), requires_payment_method (tentative
// non aboutie), canceled (échec).
export const CAUTION_RECUE_STATUSES = new Set([
  "authorized",
  "capture_pending",
  "captured",
  "charged",
  "released",
  "refunded",
  "partially_refunded",
  "refund_pending",
  "refund_scheduled",
  "refund_cancelled",
]);

// cautionRecue(resa) → booléen.
//   airbnb : caution couverte nativement par AirCover (toujours "reçue").
//   toute autre source (booking / direct / fail-safe ical-csv-inconnues) :
//   caution réelle dès que l'argent a été sécurisé/encaissé/honoré — le VRAI
//   caution_status Stripe fait foi, plus JAMAIS de filtre par source (fix B1
//   verrou livret v2 : cohérence anneau OTA ↔ verrou instructions, les sources
//   fail-safe sont traitées comme booking).
export function cautionRecue(resa) {
  if (!resa || typeof resa !== "object") return false;
  const source = normalizeSource(resa.source);

  if (source === "airbnb") return true;
  const cs = (resa.caution_status || "").toLowerCase();
  return CAUTION_RECUE_STATUSES.has(cs);
}
